import {
  Badge,
  Badge as TagBadge,
  Banner,
  Button,
  Combobox,
  DataTable,
  DefinitionList,
  DetailLayout,
  ErrorState,
  FilterToolbar,
  Modal,
  notify,
  PageHeader,
  SearchInput,
  Section,
  SegmentedControl,
  Select,
  Skeleton,
  SkeletonText,
  StatusBadge,
  StatusFilter,
  StatusPill,
  statusBadge,
  statusOptions,
  TextAreaField,
  TextField,
  Truncate,
  useConfirm,
  useDebouncedText,
  useUrlFilters,
  type Column,
  type FilterItem,
  type StatusOption,
} from "@flexfreight/ui";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { api, ApiError } from "../../api/client";
import { useConsoleMe } from "../../auth/context";
import { useStaff } from "../customers/Signups";

export type Ticket = {
  id: string;
  number: number;
  orgId: string | null;
  orgName: string | null;
  requesterName: string | null;
  requesterEmail: string | null;
  subject: string;
  status: "open" | "pending" | "on_hold" | "solved" | "closed";
  priority: "low" | "normal" | "high" | "urgent";
  queue: string;
  assigneeUserId: string | null;
  assignee: string | null;
  shipmentId: string | null;
  quoteId: string | null;
  source: string;
  tags: string[];
  /** Zoho Desk side: the Desk number and link once it exists there, else why it doesn't. */
  zohoDesk: { synced: boolean; externalId: string | null; number: string | null; url: string | null; syncedAt: string | null; error: string | null; reason: string | null };
  firstResponseDueAt: string | null;
  firstResponseAt: string | null;
  resolutionDueAt: string | null;
  solvedAt: string | null;
  lastActivityAt: string;
  createdAt: string;
  sla: "on_track" | "due_soon" | "breached" | "met" | "paused" | "none";
};
export type TicketDetail = {
  ticket: Ticket;
  /** When the ticket has no customer yet: the account the requester email belongs to (staff confirm it). */
  suggestedOrg?: { id: string; name: string } | null;
  replies: { id: string; direction: "inbound" | "outbound"; authorName: string | null; body: string; delivery: string; createdAt: string }[];
  notes: { id: string; authorName: string | null; body: string; createdAt: string }[];
};
const VIEWS = ["open", "mine", "unassigned", "breached", "solved", "all"] as const;
type View = (typeof VIEWS)[number];
const VIEW_LABEL: Record<View, string> = { open: "Open", mine: "Mine", unassigned: "Unassigned", breached: "SLA breached", solved: "Solved", all: "All" };
const QUEUES = ["support", "billing", "claims", "sales", "carrier"] as const;
const cap = (s: string) => s.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
const rel = new Intl.RelativeTimeFormat("en-US", { numeric: "auto" });
export function ago(iso: string, now = Date.now()): string {
  const mins = Math.round((Date.parse(iso) - now) / 60000);
  if (Math.abs(mins) < 60) return rel.format(mins, "minute");
  const hours = Math.round(mins / 60);
  if (Math.abs(hours) < 24) return rel.format(hours, "hour");
  return rel.format(Math.round(hours / 24), "day");
}
const timeFmt = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
const message = (e: unknown) => (e instanceof ApiError ? e.message : "Something went wrong. Try again.");
/** Only SLA states that need someone's attention get a badge in lists and headers. */
const slaAlert = (t: Ticket) => t.sla === "breached" || t.sla === "due_soon";
const plain = <V extends string>(o: { value: V; label: string }) => ({ value: o.value, label: o.label });
const STATUS_OPTIONS = statusOptions("ticket").map(plain);
const PRIORITY_OPTIONS = statusOptions("ticketPriority").map(plain);
const QUEUE_OPTIONS = QUEUES.map((x) => ({ value: x, label: cap(x) }));

/** URL filter defaults, in the order the API has always received them. The view is not counted as a filter. */
const FILTERS = { view: "open", queue: "any", priority: "any", q: "", page: 0 };
const NOT_FILTERS = ["page", "view"] as const;

/** Service > Tickets: queues by view, SLA breaches first. Filters live in the URL. */
export function TicketsPage() {
  const me = useConsoleMe();
  const navigate = useNavigate();
  const f = useUrlFilters(FILTERS, useSearchParams(), { ignoreForActive: NOT_FILTERS });
  const v = f.values;
  const view: View = (VIEWS as readonly string[]).includes(v.view) ? (v.view as View) : "open";
  const [search, setSearch] = useDebouncedText(v.q, (q) => f.set({ q }));
  const [creating, setCreating] = useState(false);
  const query = new URLSearchParams({ view, queue: v.queue, priority: v.priority, q: v.q, page: String(v.page) });
  const list = useQuery({
    queryKey: ["console", "tickets", query.toString()],
    queryFn: () => api<{ tickets: Ticket[]; counts: { open: number; mine: number; unassigned: number; breached: number }; hasMore: boolean }>(`/v1/console/tickets?${query}`),
    placeholderData: keepPreviousData,
    refetchInterval: 30_000,
  });
  const c = list.data?.counts;
  const rows = list.data?.tickets ?? [];
  const viewOptions: StatusOption<View>[] = VIEWS.map((x) => ({
    value: x,
    label: VIEW_LABEL[x],
    ...(x === "breached" ? { tone: "danger" as const } : {}),
    ...(c && x !== "solved" && x !== "all" ? { count: c[x] } : {}),
  }));
  const setView = (x: View) => f.set({ view: x });
  const items: FilterItem[] = [
    {
      key: "view",
      priority: 1,
      inline: <StatusFilter label="Ticket views" mode="inline" options={viewOptions} value={view} primary={["open", "mine", "unassigned", "breached"]} onChange={setView} countsPending={!c} />,
      panel: <StatusFilter label="View" mode="panel" options={viewOptions} value={view} primary={[]} onChange={setView} countsPending={!c} />,
      alwaysInPanel: true,
    },
    { key: "queue", priority: 2, active: v.queue !== "any", inline: <Select variant="toolbar" label="Queue" value={v.queue} active={v.queue !== "any"} onChange={(queue) => f.set({ queue })} options={[{ value: "any", label: "Any" }, ...QUEUE_OPTIONS]} /> },
    { key: "priority", priority: 3, active: v.priority !== "any", inline: <Select variant="toolbar" label="Priority" value={v.priority} active={v.priority !== "any"} onChange={(priority) => f.set({ priority })} options={[{ value: "any", label: "Any" }, ...PRIORITY_OPTIONS]} /> },
  ];
  const columns: Column<Ticket>[] = [
    {
      key: "ticket",
      header: "Ticket",
      primary: true,
      truncate: "custom",
      render: (t) => (
        <span className="ffx-cellrow">
          <Truncate text={`#${t.number} ${t.subject}`}>
            <Link className="ffa-rowlink" to={`/tickets/${t.id}`}>
              #{t.number} {t.subject}
            </Link>
          </Truncate>
          {slaAlert(t) ? <StatusBadge domain="ticketSla" value={t.sla} size="sm" /> : null}
        </span>
      ),
    },
    { key: "customer", header: "Customer", width: "14rem", render: (t) => t.orgName ?? <span className="ffa-muted">No account</span>, sub: (t) => t.requesterName ?? t.requesterEmail },
    { key: "status", header: "Status", width: "11.5rem", render: (t) => <StatusBadge domain="ticket" value={t.status} size="sm" /> },
    { key: "priority", header: "Priority", width: "7rem", render: (t) => (t.priority === "normal" || t.priority === "low" ? statusBadge("ticketPriority", t.priority).label : <StatusBadge domain="ticketPriority" value={t.priority} size="sm" />) },
    { key: "queue", header: "Queue", width: "7rem", mobile: "hide", render: (t) => cap(t.queue) },
    { key: "assignee", header: "Assignee", width: "9rem", mobile: "hide", render: (t) => t.assignee ?? <span className="ffa-muted">Unassigned</span> },
    { key: "updated", header: "Updated", width: "8rem", render: (t) => <time dateTime={t.lastActivityAt} title={timeFmt.format(new Date(t.lastActivityAt))}>{ago(t.lastActivityAt)}</time> },
  ];
  return (
    <section className="ffa-page">
      <PageHeader
        title="Tickets"
        subtitle="Customer questions from the portal and calls you log. Breached SLAs and urgent tickets come first."
        primaryAction={me.capabilities.includes("tickets:write") ? <Button variant="primary" onClick={() => setCreating(true)}>New ticket</Button> : undefined}
      />
      <FilterToolbar
        label="Ticket filters"
        search={<SearchInput label="Search number, subject, requester or customer" value={search} onChange={setSearch} />}
        items={items}
        anyActive={f.anyActive}
        showReset
        onReset={() => {
          setSearch("");
          f.set({ q: "", queue: "any", priority: "any" });
        }}
      />
      <DataTable
        caption="Tickets"
        columns={columns}
        rows={rows}
        rowKey={(t) => t.id}
        loading={list.isPending || list.isFetching}
        error={list.isError ? { title: "Couldn't load tickets", message: message(list.error), onRetry: () => void list.refetch() } : null}
        empty={f.anyActive || view !== "open" ? "No tickets match." : "No open tickets. Nice."}
        rowTone={(t) => (t.sla === "breached" ? "danger" : null)}
        onRowClick={(t) => navigate(`/tickets/${t.id}`)}
        rowHref={(t) => `/tickets/${t.id}`}
        pagination={{ page: v.page, pageSize: 50, hasMore: !!list.data?.hasMore, onPageChange: (page) => f.set({ page }) }}
      />
      {creating ? <NewTicketDialog onClose={() => setCreating(false)} /> : null}
    </section>
  );
}

function NewTicketDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [f, setF] = useState({ subject: "", body: "", requesterName: "", requesterEmail: "", priority: "normal", queue: "support" });
  const save = useMutation({
    mutationFn: () =>
      api<Ticket>("/v1/console/tickets", {
        method: "POST",
        body: { subject: f.subject.trim(), body: f.body.trim(), requesterName: f.requesterName.trim() || null, requesterEmail: f.requesterEmail.trim() || null, priority: f.priority, queue: f.queue },
      }),
    onSuccess: (t) => {
      void qc.invalidateQueries({ queryKey: ["console", "tickets"] });
      notify.success(`Ticket #${t.number} created`);
      onClose();
      navigate(`/tickets/${t.id}`);
    },
    onError: (e) => notify.error("Couldn't create the ticket", message(e)),
  });
  const emailOk = f.requesterEmail.trim() === "" || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.requesterEmail.trim());
  const valid = f.subject.trim().length >= 3 && f.body.trim() !== "" && emailOk;
  return (
    <Modal
      open
      onClose={onClose}
      title="New ticket"
      description="For a customer who called or wrote in. What they said goes in as their message."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!valid} loading={save.isPending} onClick={() => save.mutate()}>Create ticket</Button>
        </>
      }
    >
      <div className="ffx-fields">
        <TextField className="ffx-span" label="Subject" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} maxLength={200} />
        <TextAreaField className="ffx-span" label="What the customer said" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} rows={4} />
        <TextField label="Caller name" optional value={f.requesterName} onChange={(e) => setF({ ...f, requesterName: e.target.value })} maxLength={120} />
        <TextField label="Caller email" optional type="email" value={f.requesterEmail} onChange={(e) => setF({ ...f, requesterEmail: e.target.value })} error={emailOk ? undefined : "Check the email address"} />
        <Select label="Priority" value={f.priority} onChange={(v) => setF({ ...f, priority: v })} options={PRIORITY_OPTIONS} />
        <Select label="Queue" value={f.queue} onChange={(v) => setF({ ...f, queue: v })} options={QUEUE_OPTIONS} />
      </div>
    </Modal>
  );
}

type Item = { kind: "reply"; r: TicketDetail["replies"][number] } | { kind: "note"; n: TicketDetail["notes"][number] };
const back = (
  <Link to="/tickets" className="ffa-back">
    ← Tickets
  </Link>
);

/** Service > Tickets > one ticket: DetailLayout with the conversation and notes in one timeline, ticket facts beside it. */
export function TicketPage() {
  const { ticketId = "" } = useParams();
  const me = useConsoleMe();
  const qc = useQueryClient();
  const staff = useStaff();
  const q = useQuery({ queryKey: ["console", "ticket", ticketId], queryFn: () => api<TicketDetail>(`/v1/console/tickets/${ticketId}`) });
  const [mode, setMode] = useState<"reply" | "note">("reply");
  const [body, setBody] = useState("");
  const [after, setAfter] = useState<"pending" | "open" | "solved">("pending");
  const canWrite = me.capabilities.includes("tickets:write");
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["console", "ticket", ticketId] });
    void qc.invalidateQueries({ queryKey: ["console", "tickets"] });
  };
  type ReplyOut = { delivery?: string; deliveryError?: string | null; mailbox?: string | null };
  const { confirm, dialog: confirmDialog } = useConfirm();
  const send = useMutation({
    mutationFn: async (): Promise<ReplyOut> => {
      if (mode === "note") return api<ReplyOut>(`/v1/console/tickets/${ticketId}/notes`, { method: "POST", body: { body: body.trim() } });
      const post = (confirmSend?: true) => api<ReplyOut>(`/v1/console/tickets/${ticketId}/replies`, { method: "POST", body: { body: body.trim(), status: after, ...(confirmSend ? { confirmSend } : {}) } });
      try {
        return await post();
      } catch (e) {
        // Microsoft 365 sending is on: the server wants an explicit confirmation before it emails the customer.
        if (!(e instanceof ApiError) || e.reason !== "confirm_send") throw e;
        const d = e.details as { mailbox: string; to: string[]; subject: string; externalRecipients: string[] };
        const ok = await confirm({
          title: "Email this reply?",
          confirmLabel: "Send email",
          body: (
            <span className="ffa-sendcheck">
              <span className="ffa-sendcheck__row"><strong>From</strong> {d.mailbox}</span>
              <span className="ffa-sendcheck__row"><strong>To</strong> {d.to.join(", ")}</span>
              <span className="ffa-sendcheck__row"><strong>Subject</strong> {d.subject}</span>
              {d.externalRecipients.length ? <span className="ffa-sendcheck__warn" role="alert">Outside FlexFreight: {d.externalRecipients.join(", ")}.</span> : null}
            </span>
          ),
        });
        if (!ok) return { delivery: "cancelled" };
        return post(true);
      }
    },
    onSuccess: (r) => {
      if (r.delivery === "cancelled") return;
      setBody("");
      setDrafted(null);
      refresh();
      if (mode === "note") notify.success("Internal note added", "Only staff can see it.");
      else if (r.delivery === "sent") notify.success("Reply emailed", `Sent from ${r.mailbox} and shown in the customer's portal.`);
      else if (r.delivery === "failed") notify.error("Reply saved, email not sent", `${r.deliveryError ?? "Microsoft 365 didn't accept it."} The customer sees it in their portal.`);
      else notify.success("Reply saved", "The customer sees it in their portal. No email was sent.");
    },
    onError: (e) => notify.error("Couldn't save", message(e)),
  });
  // A.37: Flex AI drafts a reply into the composer. Nothing is saved or sent until a person clicks Send reply.
  const canDraft = canWrite && me.capabilities.includes("ai:use");
  const [drafted, setDrafted] = useState<{ mode: string; sources: { label: string; href: string }[] } | null>(null);
  // A.22: a solved ticket can become a staff-only knowledge base draft.
  const go = useNavigate();
  const canDraftArticle = ["ai:use", "kb:write", "tickets:read"].every((c) => me.capabilities.includes(c as never));
  const draftArticle = useMutation({
    mutationFn: () => api<{ id: string; mode: string }>("/v1/console/ai/draft-article", { method: "POST", body: { ticketId } }),
    onSuccess: (r) => {
      notify.success("Draft article created", r.mode === "demonstration" ? "A template from this ticket (no AI key connected). Review it before publishing." : "Staff-only until you publish it.");
      go(`/knowledge/${r.id}`);
    },
    onError: (e) => notify.error("Couldn't draft an article", message(e)),
  });
  const draft = useMutation({
    mutationFn: () => api<{ mode: "live" | "demonstration"; text: string; sources: { label: string; href: string }[] }>("/v1/console/ai/draft-reply", { method: "POST", body: { ticketId } }),
    onSuccess: (r) => {
      setBody(r.text);
      setDrafted({ mode: r.mode, sources: r.sources });
    },
    onError: (e) => notify.error("Couldn't draft a reply", message(e)),
  });
  const startDraft = async () => {
    if (body.trim() && !(await confirm({ title: "Replace what you've written?", body: "The Flex AI draft replaces the text in the reply box.", confirmLabel: "Replace", cancelLabel: "Keep mine" }))) return;
    draft.mutate();
  };
  // A.21: articles that may answer this ticket, from its subject and the customer's first message.
  const seed = q.data ? `${q.data.ticket.subject} ${q.data.replies.find((r) => r.direction === "inbound")?.body ?? ""}`.slice(0, 2000) : "";
  const suggest = useQuery({
    queryKey: ["console", "kb", "suggest", ticketId, seed.length],
    queryFn: () => api<{ articles: { id: string; title: string; summary: string | null; visibility: string }[] }>(`/v1/console/kb/suggest?${new URLSearchParams({ text: seed })}`),
    enabled: seed.length > 0 && me.capabilities.includes("kb:read"),
  });
  const update = useMutation({
    mutationFn: (patch: Record<string, unknown>) => api<Ticket>(`/v1/console/tickets/${ticketId}`, { method: "PATCH", body: patch }),
    onSuccess: (t, patch) => {
      refresh();
      if ("tags" in patch && t.zohoDesk.error) notify.error("Tags saved, not synced to Zoho Desk", t.zohoDesk.error);
    },
    onError: (e) => notify.error("Couldn't update the ticket", message(e)),
  });
  const tagsInUse = useQuery({ queryKey: ["console", "tickets", "tags"], queryFn: () => api<{ tags: { tag: string; count: number }[] }>("/v1/console/tickets/tags"), enabled: canWrite });
  if (q.isPending) {
    return (
      <section className="ffa-page" aria-busy="true">
        <DetailLayout header={<PageHeader back={back} eyebrow="Ticket" title={<Skeleton width="20rem" height="2.25rem" />} subtitle={<Skeleton width="16rem" />} />} aside={<Section title="Ticket"><SkeletonText lines={5} /></Section>} asideLabel="Ticket details">
          <Section><SkeletonText lines={6} /></Section>
        </DetailLayout>
        <span className="ffx-sr">Loading ticket…</span>
      </section>
    );
  }
  if (q.isError || !q.data) {
    const missing = q.error instanceof ApiError && q.error.status === 404;
    return (
      <section className="ffa-page">
        <PageHeader back={back} title={missing ? "Ticket not found" : "Ticket"} />
        {missing ? <Banner tone="warning" title="This ticket doesn't exist.">It may have been merged or removed, or the link is wrong.</Banner> : <ErrorState title="Couldn't load this ticket" message={message(q.error)} onRetry={() => void q.refetch()} />}
      </section>
    );
  }
  const { ticket: t, replies, notes } = q.data;
  const items: Item[] = [...replies.map((r) => ({ kind: "reply" as const, r })), ...notes.map((n) => ({ kind: "note" as const, n }))].sort((a, b) => ((a.kind === "reply" ? a.r.createdAt : a.n.createdAt) < (b.kind === "reply" ? b.r.createdAt : b.n.createdAt) ? -1 : 1));
  const suggested = q.data.suggestedOrg;
  return (
    <section className="ffa-page">
      <DetailLayout
        header={
          <PageHeader
            back={back}
            eyebrow={`Ticket #${t.number}`}
            title={t.subject}
            badges={
              <>
                <StatusBadge domain="ticket" value={t.status} />
                {slaAlert(t) ? <StatusBadge domain="ticketSla" value={t.sla} /> : null}
              </>
            }
            subtitle={
              <>
                {t.requesterName ?? t.requesterEmail ?? "Unknown requester"}
                {t.orgId ? <> · <Link to={`/customers/${t.orgId}`}>{t.orgName}</Link></> : " · No account"} · opened {ago(t.createdAt)} from {t.source === "portal" ? "the portal" : t.source}
              </>
            }
            secondaryActions={canDraftArticle && (t.status === "solved" || t.status === "closed") ? <Button onClick={() => draftArticle.mutate()} loading={draftArticle.isPending}>Draft help article</Button> : null}
          />
        }
        notices={
          canWrite && !t.orgId && suggested ? (
            <Banner tone="info" actions={<Button size="sm" loading={update.isPending} onClick={() => update.mutate({ orgId: suggested.id })}>Link to {suggested.name}</Button>}>
              {t.requesterEmail} is a user on <strong>{suggested.name}</strong>. Link this ticket so the customer can see it in their portal?
            </Banner>
          ) : null
        }
        aside={
          <>
            <Section title="Ticket">
              {canWrite ? (
                <div className="ffx-fields">
                  <Select size="sm" label="Status" value={t.status} onChange={(v) => update.mutate({ status: v })} options={STATUS_OPTIONS} />
                  <Select size="sm" label="Priority" value={t.priority} onChange={(v) => update.mutate({ priority: v })} options={PRIORITY_OPTIONS} />
                  <Select size="sm" label="Queue" value={t.queue} onChange={(v) => update.mutate({ queue: v })} options={QUEUE_OPTIONS} />
                  <Select size="sm" label="Assignee" value={t.assigneeUserId ?? ""} onChange={(v) => update.mutate({ assigneeUserId: v || null })} options={[{ value: "", label: "Unassigned" }, ...(staff.data?.staff ?? []).map((s) => ({ value: s.id, label: s.name || s.email }))]} />
                </div>
              ) : null}
              <DefinitionList
                items={[
                  !canWrite && { term: "Priority", detail: statusBadge("ticketPriority", t.priority).label },
                  !canWrite && { term: "Queue", detail: cap(t.queue) },
                  !canWrite && { term: "Assignee", detail: t.assignee ?? "Unassigned" },
                  { term: "First response due", detail: t.firstResponseAt ? `Answered ${ago(t.firstResponseAt)}` : t.firstResponseDueAt ? timeFmt.format(new Date(t.firstResponseDueAt)) : "-" },
                  { term: "Resolution due", detail: t.solvedAt ? `Solved ${ago(t.solvedAt)}` : t.resolutionDueAt ? timeFmt.format(new Date(t.resolutionDueAt)) : "-" },
                  t.shipmentId ? { term: "Shipment", detail: <Link to={`/shipments?bucket=all${t.orgId ? `&org=${t.orgId}` : ""}`}>Open shipments</Link> } : null,
                  { term: "Requester", detail: [t.requesterName, t.requesterEmail].filter(Boolean).join(" · ") || "-" },
                  {
                    term: "Zoho Desk",
                    detail: t.zohoDesk.synced ? (
                      <span className="ffa-desk">
                        <StatusPill tone="success">Synced with Zoho Desk</StatusPill>
                        {t.zohoDesk.number ? (t.zohoDesk.url ? <a href={t.zohoDesk.url} target="_blank" rel="noopener noreferrer" className="ffx-num">#{t.zohoDesk.number}</a> : <span className="ffx-num">#{t.zohoDesk.number}</span>) : null}
                        {t.zohoDesk.error ? <span className="ffa-small ffa-warntext">{t.zohoDesk.error}</span> : null}
                      </span>
                    ) : (
                      <span className="ffa-desk">
                        <StatusPill tone={t.zohoDesk.error ? "warning" : "neutral"}>Not synced</StatusPill>
                        <span className="ffa-small ffa-muted">{t.zohoDesk.reason}</span>
                      </span>
                    ),
                  },
                ]}
              />
            </Section>
            <Section title="Tags">
              <TagsEditor tags={t.tags} canWrite={canWrite} known={tagsInUse.data?.tags.map((x) => x.tag) ?? []} onChange={(tags) => update.mutate({ tags })} busy={update.isPending} />
            </Section>
            {suggest.data?.articles.length ? (
              <Section title="Suggested articles">
                <ul className="ffa-plainlist ffa-suggest" aria-label="Suggested articles">
                  {suggest.data.articles.map((a) => (
                    <li key={a.id}>
                      <Link to={`/knowledge/${a.id}`} className="ffa-rowlink">{a.title}</Link>
                      {a.visibility === "public" && canWrite && t.status !== "closed" ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setMode("reply");
                            setBody((b) => `${b ? `${b}\n\n` : ""}${a.title}${a.summary ? `: ${a.summary}` : ""}\n(Help center article: "${a.title}")`);
                          }}
                        >
                          Add to reply
                        </Button>
                      ) : (
                        <Badge size="sm" dot={false}>Staff only</Badge>
                      )}
                    </li>
                  ))}
                </ul>
              </Section>
            ) : null}
          </>
        }
        asideLabel="Ticket details"
      >
        <ol className="ffa-thread" aria-label="Conversation and internal notes">
          {items.map((it) =>
            it.kind === "reply" ? (
              <li key={it.r.id} className={`ffa-msg ffa-msg--${it.r.direction}`}>
                <div className="ffa-msg__meta">
                  <strong>{it.r.authorName ?? (it.r.direction === "inbound" ? "Customer" : "FlexFreight")}</strong>
                  <span className="ffa-muted">{it.r.direction === "inbound" ? "Customer" : "Reply to customer"} · <time dateTime={it.r.createdAt}>{timeFmt.format(new Date(it.r.createdAt))}</time></span>
                  {it.r.direction === "outbound" && it.r.delivery === "not_sent" ? <span className="ffa-muted ffa-small">Shown in portal · email not sent</span> : null}
                  {it.r.direction === "outbound" && it.r.delivery === "sent" ? <span className="ffa-muted ffa-small">Shown in portal · emailed</span> : null}
                  {it.r.direction === "outbound" && (it.r.delivery === "failed" || it.r.delivery === "sending") ? <span className="ffa-small ffa-warntext">Shown in portal · email not sent (Microsoft 365 didn't accept it)</span> : null}
                </div>
                <p className="ffa-msg__body">{it.r.body}</p>
              </li>
            ) : (
              <li key={it.n.id} className="ffa-msg ffa-msg--note">
                <div className="ffa-msg__meta">
                  <Badge tone="warning" size="sm" dot={false}>Internal note</Badge>
                  <strong>{it.n.authorName ?? "Staff"}</strong>
                  <span className="ffa-muted"><time dateTime={it.n.createdAt}>{timeFmt.format(new Date(it.n.createdAt))}</time></span>
                </div>
                <p className="ffa-msg__body">{it.n.body}</p>
              </li>
            ),
          )}
        </ol>
        {canWrite && t.status !== "closed" ? (
          <Section className={`ffa-composer ffa-composer--${mode}`}>
            <form
              className="ffa-composer__form"
              onSubmit={(e) => {
                e.preventDefault();
                if (body.trim()) send.mutate();
              }}
            >
              <SegmentedControl label="Message type" options={[{ value: "reply", label: "Reply to customer" }, { value: "note", label: "Internal note" }]} value={mode} onChange={setMode} />
              <p className={mode === "note" ? "ffa-composer__hint ffa-composer__hint--note" : "ffa-composer__hint"} aria-live="polite">
                {mode === "note" ? "Only FlexFreight staff will see this note." : `${t.requesterName ?? "The customer"} will see this in their portal.`}
              </p>
              <TextAreaField label={mode === "note" ? "Internal note" : "Reply"} value={body} onChange={(e) => setBody(e.target.value)} rows={drafted && mode === "reply" ? 9 : 5} maxLength={20000} />
              {drafted && mode === "reply" ? (
                <p className="ffa-composer__hint" role="status">
                  {drafted.mode === "demonstration" ? "Template draft (demonstration mode: no AI key connected)." : "Drafted by Flex AI."} Review and edit before sending.
                  {drafted.sources.length ? ` Based on: ${drafted.sources.map((x) => x.label).join(", ")}.` : ""}
                </p>
              ) : null}
              <div className="ffa-composer__actions">
                {mode === "reply" ? (
                  <Select size="sm" label="Then set status to" value={after} onChange={(v) => setAfter(v)} options={[{ value: "pending", label: "Waiting on customer" }, { value: "open", label: "Open" }, { value: "solved", label: "Solved" }]} />
                ) : null}
                <span className="ffa-composer__buttons">
                  {mode === "reply" && canDraft ? (
                    <Button type="button" onClick={() => void startDraft()} loading={draft.isPending}>Draft with Flex AI</Button>
                  ) : null}
                  <Button type="submit" variant="primary" disabled={!body.trim()} loading={send.isPending}>{mode === "note" ? "Add internal note" : "Send reply"}</Button>
                </span>
              </div>
            </form>
          </Section>
        ) : null}
      </DetailLayout>
      {confirmDialog}
    </section>
  );
}

/** Tags on a ticket: chips with a remove button, and a combobox over the tags already in use (type a new one to add it). */
function TagsEditor({ tags, canWrite, known, onChange, busy }: { tags: string[]; canWrite: boolean; known: string[]; onChange: (tags: string[]) => void; busy: boolean }) {
  const [seq, setSeq] = useState(0);
  const normalize = (t: string) => t.trim().toLowerCase().replace(/[,\s]+/g, " ").slice(0, 40);
  const add = (raw: string) => {
    const tag = normalize(raw);
    if (!tag || tags.includes(tag)) return;
    onChange([...tags, tag]);
    setSeq((n) => n + 1);
  };
  return (
    <div className="ffa-tags">
      <ul className="ffa-tags__list" aria-label="Tags">
        {tags.length === 0 ? <li className="ffa-muted ffa-small">No tags</li> : null}
        {tags.map((tag) => (
          <li key={tag} className="ffa-tags__item">
            <TagBadge size="sm" dot={false}>{tag}</TagBadge>
            {canWrite ? (
              <button type="button" className="ffa-tags__remove" aria-label={`Remove tag ${tag}`} disabled={busy} onClick={() => onChange(tags.filter((x) => x !== tag))}>
                ×
              </button>
            ) : null}
          </li>
        ))}
      </ul>
      {canWrite ? (
        <Combobox
          key={seq}
          label="Add a tag"
          size="sm"
          placeholder="Type to search or add"
          minChars={1}
          icon={false}
          emptyText="Type a tag and press Enter to add it."
          load={async (q) => {
            const typed = normalize(q);
            const hits = known.filter((k) => !tags.includes(k) && k.includes(typed)).slice(0, 8).map((k) => ({ value: k, label: k }));
            return typed && !hits.some((h) => h.value === typed) ? [...hits, { value: typed, label: `Add "${typed}"` }] : hits;
          }}
          onSelect={(o) => add(o.value)}
          disabled={busy}
        />
      ) : null}
    </div>
  );
}
